import {Component} from '@angular/core';
import {BoulderListService} from '../../services/boulder-list.service';
import {ActivatedRoute} from '@angular/router';
import {AuthService} from '../../services/auth.service';
import {BBError} from '../../errors/bb-error';
import {GymService} from '../../services/gym.service';
import {ErrorNotificationService} from '../../services/error-notification.service';
import {firstValueFrom} from 'rxjs';
import {MatomoTracker} from 'ngx-matomo-client';
import {BoulderList} from '../../model/boulder-list';
import {GuestInfoComponent} from '../../components/guest-info/guest-info.component';
import {AlertController, ModalController} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import {GuestService} from '../../services/guest-service';

@Component({
  selector: 'app-boulder-lists',
  templateUrl: './boulder-lists.page.html',
  styleUrls: ['./boulder-lists.page.scss'],
})
export class BoulderListsPage {

  activeSegment = 'private';
  boulderLists: BoulderList[] = [];
  numOwnLists = -1;
  isLoading = false;

  constructor(
    private matomoTracker: MatomoTracker,
    private activatedRoute: ActivatedRoute,
    private errorNotificationService: ErrorNotificationService,
    private gymService: GymService,
    private authService: AuthService,
    private boulderListService: BoulderListService,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private translateService: TranslateService,
    public guestService: GuestService) {
    this.activatedRoute.queryParams.subscribe(async params => {
      if (params['reload']) {
        await this.reloadLists();
      }
      if (params['sync']) {
        await this.onRefresh(null);
      }
    });
  }

  ionViewWillEnter() {
    this.reloadLists();
    this.matomoTracker.trackPageView('boulder-lists');
  }

  private async reloadLists() {
    if (this.guestService.isGuest()) {
      this.activeSegment = 'public';
    }
    this.isLoading = true;
    try {
      const allLists = await firstValueFrom(this.boulderListService.getOwnAndPublicBoulderLists$());
      this.boulderLists = allLists.filter(l =>
        (this.activeSegment == 'private' && l.author == this.authService.getCurrentUsername()) ||
        (l.publicList && this.activeSegment == 'public'));
      this.numOwnLists = this.boulderLists.filter(b => b.author == this.authService.getCurrentUsername()).length;
    } finally {
      this.isLoading = false;
    }
  }

  segmentChanged($event: Event) {
    this.activeSegment = ($event as CustomEvent).detail.value;
    this.reloadLists();
  }

  onSwipeRight() {
    this.activeSegment = 'private';
    this.reloadLists();
  }

  onSwipeLeft() {
    this.activeSegment = 'public';
    this.reloadLists();
  }


  async onRefresh(event) {
    try {
      await this.gymService.syncActiveGym();
    } catch (err) {
      if (!(err as BBError).bbErrorType) {
        throw err;
      }
      await this.errorNotificationService.showBBError(err);
    } finally {
      event?.target?.complete();
      await this.reloadLists();
    }
  }

  async createNewList() {
    if (this.guestService.isGuest()) {
      GuestInfoComponent.showAsModal(this.modalCtrl);
      return;
    }
    const alert = await this.alertCtrl.create({
      header: this.translateService.instant('add-to-boulder-list-page.create-new-list'),
      inputs: [
        {
          name: 'name',
          cssClass: 'create-list',
          type: 'text',
          placeholder: this.translateService.instant('add-to-boulder-list-page.name-for-new-list'),
          attributes: {
            minLength: 3,
            maxLength: 100
          }
        }
      ],
      buttons: [
        {
          text: this.translateService.instant('add-to-boulder-list-page.public'),
          handler: async (data) => {
            if (data?.name?.length > 2) {
              await this.boulderListService.createNewList(data.name, true);
              await this.reloadLists();
            }
          }
        },
        {
          text: this.translateService.instant('add-to-boulder-list-page.private'),
          handler: async (data) => {
            await this.boulderListService.createNewList(data.name, false);
            await this.reloadLists();
          }
        }
      ]
    });

    await alert.present();
  }
}
